import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveReportDraft,
  fetchReportDraft,
  fetchReportDraftVersions,
  clearReportDraft,
  clearSaveDraftStatus
} from '../actions';

/**
 * Custom hook for managing report drafts
 * Provides functions to save, load, and manage draft state for all report types
 */
export const useReportDraft = () => {
  const dispatch = useDispatch();

  // Select state from Redux store
  const {
    draft,
    draftSignatures,
    draftId,
    version,
    status,
    createdAt,
    updatedAt,
    isReadyToGenerate,
    missingFields,
    isFetchingDraft,
    isSavingDraft,
    fetchDraftError,
    saveDraftError,
    saveDraftSuccess,
    draftVersions,
    isFetchingVersions,
    fetchVersionsError
  } = useSelector((state) => state.reportDrafts || {});

  /**
   * Save a draft with form data and signatures
   * @param {Object} payload - Draft payload
   * @param {number} payload.projectId - Project ID
   * @param {string} payload.reportType - Report type (e.g., 'INSPECTION_RELEASE_NOTE')
   * @param {number} [payload.pipeId] - Pipe ID (for pipe-level reports)
   * @param {number[]} [payload.pipeIds] - Pipe IDs (for multi-pipe reports)
   * @param {number} [payload.productionId] - Production ID (for production-level reports)
   * @param {Object} payload.formData - All form inputs
   * @param {Object} payload.signatures - Signature data for each signer
   */
  const saveDraft = useCallback((payload) => {
    dispatch(saveReportDraft(payload));
  }, [dispatch]);

  /**
   * Load an existing draft
   * @param {Object} params - Query parameters
   * @param {number} params.projectId - Project ID
   * @param {string} params.reportType - Report type
   * @param {number} [params.pipeId] - Pipe ID
   * @param {number[]} [params.pipeIds] - Pipe IDs
   * @param {number} [params.productionId] - Production ID
   */
  const loadDraft = useCallback((params) => {
    dispatch(fetchReportDraft(params));
  }, [dispatch]);

  /**
   * Load all versions of a draft
   * @param {Object} params - Query parameters (same as loadDraft)
   */
  const loadDraftVersions = useCallback((params) => {
    dispatch(fetchReportDraftVersions(params));
  }, [dispatch]);

  /**
   * Clear all draft state (call when modal closes)
   */
  const clearDraft = useCallback(() => {
    dispatch(clearReportDraft());
  }, [dispatch]);

  /**
   * Clear only the save status (success/error) without clearing draft data
   */
  const clearSaveStatus = useCallback(() => {
    dispatch(clearSaveDraftStatus());
  }, [dispatch]);

  /**
   * Helper to build signatures object for API
   * @param {Object} signatures - Raw signature data from form
   * @returns {Object} - Formatted signatures object
   */
  const buildSignaturesPayload = useCallback((signatures) => {
    const payload = {};

    if (signatures.qc) {
      payload.qc = {
        name: signatures.qc.name || '',
        signature: signatures.qc.signature || '',
        stampImage: signatures.qc.stampImage || '',
        timestamp: signatures.qc.timestamp || new Date().toISOString(),
        signed: !!signatures.qc.signature
      };
    }

    if (signatures.customer) {
      payload.customer = {
        name: signatures.customer.name || '',
        signature: signatures.customer.signature || '',
        stampImage: signatures.customer.stampImage || '',
        timestamp: signatures.customer.timestamp || new Date().toISOString(),
        signed: !!signatures.customer.signature
      };
    }

    if (signatures.projectManager) {
      payload.projectManager = {
        name: signatures.projectManager.name || '',
        signature: signatures.projectManager.signature || '',
        stampImage: signatures.projectManager.stampImage || '',
        timestamp: signatures.projectManager.timestamp || new Date().toISOString(),
        signed: !!signatures.projectManager.signature
      };
    }

    return payload;
  }, []);

  return {
    // State - form data
    draft,
    // State - signatures (stored separately)
    draftSignatures,
    // State - metadata
    draftId,
    version,
    status,
    createdAt,
    updatedAt,
    isReadyToGenerate,
    missingFields,
    draftVersions,

    // Loading states
    isFetchingDraft,
    isSavingDraft,
    isFetchingVersions,

    // Error states
    fetchDraftError,
    saveDraftError,
    fetchVersionsError,

    // Success state
    saveDraftSuccess,

    // Actions
    saveDraft,
    loadDraft,
    loadDraftVersions,
    clearDraft,
    clearSaveStatus,

    // Helpers
    buildSignaturesPayload
  };
};

export default useReportDraft;

